<template>
    <div class="catalogue" @click="followLink">
        <section ref="hero" class="catalogue-hero px-2">
            <div class="row justify-content-center no-gutters">
                <div class="col-12 col-md-11 col-lg-10 col-xl-9 col-xxxl-7 text-center">
                    <div class="catalogue-title">
                        <span class="catalogue-eyebrow">{{ $t("catalogue.eyebrow") }}</span>
                        <h1>{{ catalogue.title }}</h1>
                    </div>
                    <!-- the catalogue html comes from our .md, rendered without raw html and with safe urls only (sanctions/utils/catalogue.py) -->
                    <!-- nosemgrep: javascript.vue.security.audit.xss.templates.avoid-v-html.avoid-v-html -->
                    <div class="catalogue-intro" v-html="catalogue.intro"></div>
                </div>
            </div>
        </section>

        <div :class="['catalogue-controls px-2', { stuck }]">
            <div class="row justify-content-center no-gutters">
                <div class="col-12 col-md-11 col-lg-10 col-xl-9 col-xxxl-7 catalogue-controls-inner">
                    <div class="catalogue-finder">
                        <div class="catalogue-search">
                            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor"
                                stroke-width="2"
                            >
                                <circle cx="11" cy="11" r="7"></circle>
                                <path d="M20 20l-3.5-3.5"></path>
                            </svg>
                            <input v-model="query" type="search" :placeholder="$t('catalogue.search')">
                        </div>
                        <catalogue-filter :audiences="audiences" :selected="selectedAudiences" :counts="audienceCounts"
                            @toggle="toggleAudience"
                        />
                    </div>
                </div>
            </div>
        </div>

        <div class="catalogue-body px-2">
            <div class="row justify-content-center no-gutters">
                <div class="col-12 col-md-11 col-lg-10 col-xl-9 col-xxxl-7">
                    <div class="catalogue-groups">
                        <!-- one band per group, alternating like the sections of about_sanctions -->
                        <section v-for="(group, index) in visibleGroups" :key="group.slug" :id="group.slug"
                            :class="['catalogue-group', index % 2 ? 'section' : 'dark_section']"
                        >
                            <h2 class="catalogue-group-title">{{ group.title }}</h2>
                            <!-- nosemgrep: javascript.vue.security.audit.xss.templates.avoid-v-html.avoid-v-html -->
                            <div class="catalogue-group-intro" v-html="group.intro"></div>

                            <article v-for="risk in group.risks" :key="risk.slug" :id="risk.slug"
                                :class="['catalogue-risk', { open: isOpen(risk) }]"
                            >
                                <h3 class="catalogue-risk-heading">
                                    <button class="catalogue-risk-header" :aria-expanded="isOpen(risk)" @click="toggle(risk)">
                                        <!-- the dot of the certificates, a risk without a severity is one of the others, grey -->
                                        <span :class="['severity-dot', 'severity-' + (risk.severity || 'others')]"
                                            :title="$t('catalogue.severity.' + (risk.severity || 'others'))"
                                        ></span>
                                        <span class="catalogue-risk-text">
                                            <span class="catalogue-risk-title">{{ risk.title }}</span>
                                            <!-- the one line a client scans before deciding to open the risk -->
                                            <span v-if="risk.summary" class="catalogue-risk-summary">{{ risk.summary }}</span>
                                        </span>
                                        <span class="catalogue-risk-caret">
                                            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
                                                stroke-width="2.5"
                                            >
                                                <path d="M6 9l6 6 6-6"></path>
                                            </svg>
                                        </span>
                                    </button>
                                </h3>

                                <!-- v-show keeps the text of closed risks in the page for search engines -->
                                <div v-show="isOpen(risk)" class="catalogue-risk-body">
                                    <!-- who we screen, the api flag category slugs, the severity -->
                                    <dl class="catalogue-risk-meta">
                                        <div v-if="risk.audiences.length">
                                            <dt>{{ $t("catalogue.audience_label") }}</dt>
                                            <dd>
                                                <span v-for="audience in risk.audiences" :key="audience" class="catalogue-meta-audience">
                                                    <catalogue-audience :audience="audience" />
                                                    {{ $t("catalogue.audience." + audience) }}
                                                </span>
                                            </dd>
                                        </div>
                                        <div v-if="risk.flags.length">
                                            <dt>{{ $t("catalogue.flags_label") }}</dt>
                                            <dd><code v-for="flag in risk.flags" :key="flag">{{ flag }}</code></dd>
                                        </div>
                                        <div>
                                            <dt>{{ $t("catalogue.severity_label") }}</dt>
                                            <dd>
                                                <span :class="['severity-dot', 'severity-' + (risk.severity || 'others')]"></span>
                                                {{ $t("catalogue.severity." + (risk.severity || "others")) }}
                                            </dd>
                                        </div>
                                    </dl>
                                    <!-- nosemgrep: javascript.vue.security.audit.xss.templates.avoid-v-html.avoid-v-html -->
                                    <div v-if="risk.intro" class="catalogue-risk-intro" v-html="risk.intro"></div>
                                    <div class="catalogue-sections">
                                        <div v-for="section in risk.sections" :key="section.title"
                                            :class="['catalogue-section', section.style && 'catalogue-section-' + section.style]"
                                        >
                                            <h4>{{ section.title }}</h4>
                                            <!-- nosemgrep: javascript.vue.security.audit.xss.templates.avoid-v-html.avoid-v-html -->
                                            <div v-html="section.content"></div>
                                        </div>
                                    </div>
                                </div>
                            </article>
                        </section>

                        <section v-if="!visibleGroups.length" class="catalogue-group dark_section catalogue-empty">
                            <span class="catalogue-empty-hand">{{ $t("catalogue.nothing_found") }}</span>
                        </section>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import CatalogueAudience from "./CatalogueAudience.vue";
import CatalogueFilter from "./CatalogueFilter.vue";

// the screenings of the for= in catalogue.md, in the order of the filter
const AUDIENCES = ["cz", "sk", "international", "person"];

// lower case without diacritics, so "rizeni" finds "řízení"
function normalize(text) {
    return text.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

// on a first visit the webfonts are not cached yet and swap in after the app is up, making the texts above a risk
// taller or shorter - a scroll started before that lands off the risk
const pageSettled = new Promise(resolve => {
    if (document.readyState === "complete") {
        resolve();
    } else {
        window.addEventListener("load", resolve, { once: true });
    }
}).then(() => document.fonts.ready);

export default {
    components: { CatalogueAudience, CatalogueFilter },
    data: function () {
        return {
            catalogue: { title: "", intro: "", groups: [], url: "", open: null },
            query: "",
            audiences: AUDIENCES,
            selectedAudiences: [],
            openRisks: [],
            stuck: false
        };
    },
    computed: {
        allRisks() {
            return this.catalogue.groups.flatMap(group => group.risks);
        },
        // a risk's address carries its group, as on popisrizik: /katalog/<group>/<risk>
        groupOfRisk() {
            const groups = {};
            this.catalogue.groups.forEach(group => group.risks.forEach(risk => {
                groups[risk.slug] = group.slug;
            }));
            return groups;
        },
        // everything a risk can be found by - its title, summary, flag codes and the text of its note and sections
        searchIndex() {
            const index = {};
            this.allRisks.forEach(risk => {
                const sections = risk.sections.map(section => section.title + " " + section.content).join(" ");
                const text = [risk.title, risk.summary, risk.flags.join(" "), risk.intro, sections].join(" ");
                index[risk.slug] = normalize(text.replace(/<[^>]+>/g, " "));
            });
            return index;
        },
        // how many risks we can screen for each screening, next to it in the filter
        audienceCounts() {
            const counts = {};
            AUDIENCES.forEach(audience => {
                counts[audience] = this.allRisks.filter(risk => risk.audiences.includes(audience)).length;
            });
            return counts;
        },
        // the groups with the risks the search finds - more picked screenings show the risks of any of them
        visibleGroups() {
            const query = normalize(this.query.trim());
            const audiences = this.selectedAudiences;
            return this.catalogue.groups.map(group => ({
                ...group,
                risks: group.risks.filter(risk =>
                    (!query || this.searchIndex[risk.slug].includes(query))
                    && (!audiences.length || risk.audiences.some(audience => audiences.includes(audience)))
                )
            })).filter(group => group.risks.length);
        }
    },

    mounted: function () {
        if (document.getElementById("language")) {
            this.$i18n.locale = document.getElementById("language").innerText;
        }

        this.catalogue = JSON.parse(document.getElementById("catalogue_data").textContent);

        this.$nextTick(() => {
            if (this.catalogue.open) {
                this.openCode(this.catalogue.open);
            }
        });
        window.addEventListener("popstate", this.openFromPath);

        // the search bar sticks once the hero above it has scrolled off the top of the screen - the line under it
        // waits 40px more, until the gap above the first band (.catalogue-body) has gone under the bar too
        this.stuckObserver = new IntersectionObserver(([entry]) => {
            this.stuck = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
        }, { rootMargin: "40px 0px 0px 0px" });
        this.stuckObserver.observe(this.$refs.hero);
    },
    unmounted: function () {
        window.removeEventListener("popstate", this.openFromPath);
        this.stuckObserver.disconnect();
    },
    methods: {
        isOpen(risk) {
            return this.openRisks.includes(risk.slug);
        },
        toggle(risk) {
            if (this.isOpen(risk)) {
                this.openRisks = this.openRisks.filter(slug => slug !== risk.slug);
                return;
            }
            this.openRisks.push(risk.slug);
            // the url always points at the last opened risk, so it can be copied and shared
            history.replaceState(null, "", this.urlFor(risk.slug));
        },
        toggleAudience(audience) {
            if (this.selectedAudiences.includes(audience)) {
                this.selectedAudiences = this.selectedAudiences.filter(selected => selected !== audience);
            } else {
                this.selectedAudiences.push(audience);
            }
        },
        scrollTo(id) {
            document.getElementById(id).scrollIntoView({ behavior: "smooth", block: "start" });
        },
        urlFor(slug) {
            const group = this.groupOfRisk[slug];
            return this.catalogue.url + "/" + (group ? group + "/" + slug : slug);
        },
        // /katalog/<group> gives the group, /katalog/<group>/<risk> the risk - the path comes url-encoded
        codeFromPath(pathname) {
            const prefix = this.catalogue.url + "/";
            const path = decodeURIComponent(pathname);
            return path.startsWith(prefix) ? path.slice(prefix.length).split("/").pop() : null;
        },
        // links in the texts (/katalog/aml-rizika) open in place instead of loading the page again -
        // a ctrl/cmd click still opens a new tab
        followLink(event) {
            const link = event.target.closest("a");
            if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey
                || event.shiftKey || link.origin !== window.location.origin) {
                return;
            }
            const code = this.codeFromPath(link.pathname);
            if (!code) {
                return;
            }
            event.preventDefault();
            history.pushState(null, "", link.pathname);
            this.openCode(code);
        },
        // back and forward step through the links followed above
        openFromPath() {
            const code = this.codeFromPath(window.location.pathname);
            if (code) {
                this.openCode(code);
            }
        },
        // a risk's address opens that risk and a group's address opens the whole group, so sales can
        // send a client straight to it - the server already checked the address exists
        openCode(code) {
            const group = this.catalogue.groups.find(group => group.slug === code);
            const risk = this.allRisks.find(risk => risk.slug === code);
            if (!group && !risk) {
                return;
            }
            // a search or the filter could be hiding it
            this.query = "";
            this.selectedAudiences = [];
            const slugs = group ? group.risks.map(risk => risk.slug) : [risk.slug];
            this.openRisks = [...new Set([...this.openRisks, ...slugs])];
            Promise.all([pageSettled, this.$nextTick()]).then(() => this.scrollTo(code));
        }
    }
};
</script>
